
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

export default function Header() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('jhigu_cart') || '[]');
        setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        setCartCount(0);
      }
    };

    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-rose-600' : 'text-white'}`}>
              Jhigu
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/')
                  ? 'text-rose-600'
                  : isScrolled
                  ? 'text-gray-700 hover:text-rose-600'
                  : 'text-white hover:text-rose-200'
              }`}
            >
              Home
            </Link>
            <Link
              to="/shop"
              className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/shop')
                  ? 'text-rose-600'
                  : isScrolled
                  ? 'text-gray-700 hover:text-rose-600'
                  : 'text-white hover:text-rose-200'
              }`}
            >
              Shop
            </Link>
            <Link
              to="/contact"
              className={`font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive('/contact')
                  ? 'text-rose-600'
                  : isScrolled
                  ? 'text-gray-700 hover:text-rose-600'
                  : 'text-white hover:text-rose-200'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            <Link
              to="/cart"
              className={`relative p-2 rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'hover:bg-gray-100 text-gray-700'
                  : 'hover:bg-white/10 text-white'
              }`}
            >
              <i className="ri-shopping-cart-line text-xl"></i>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center space-x-2 p-2 rounded-full transition-colors cursor-pointer ${
                    isScrolled
                      ? 'hover:bg-gray-100 text-gray-700'
                      : 'hover:bg-white/10 text-white'
                  }`}
                >
                  <i className="ri-user-line text-xl"></i>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2">
                    <Link
                      to="/my-orders"
                      onClick={() => setShowUserMenu(false)}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shopping-bag-line mr-2"></i>
                      My Orders
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-logout-box-line mr-2"></i>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className={`px-6 py-2 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? 'bg-rose-600 hover:bg-rose-700 text-white'
                    : 'bg-white hover:bg-gray-100 text-rose-600'
                }`}
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
